/**
 * DiagnosticoNuvemPage - N226 (Logs > Diagnostico Nuvem/App)
 *
 * Quanto o servidor demora, quando ele satura (simultaneas >= lugares do
 * gunicorn) e quanto de cada sincronizacao do app e servidor x rede/app.
 * Nasceu do N225: a fila de producao era invisivel. Leitura MASTER-only no
 * backend (T30); o guard abaixo so evita a tela vazia de 403.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Clock,
  CloudCog,
  Gauge,
  RefreshCw,
  Server,
  Timer,
} from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { MainLayout } from '../components/MainLayout';
import { StatCard } from '../components/StatCard';
import { GraficoPorHora } from '../components/diagnosticoNuvem/GraficoPorHora';
import { SincronizacoesTabela } from '../components/diagnosticoNuvem/SincronizacoesTabela';
import { Tabela } from '../components/diagnosticoNuvem/Tabela';
import useAuthStore from '../stores/authStore';
import { formatarQuantidade } from '../utils/formatters';
import { MESSAGES } from '../utils/messages';
import {
  Origem,
  ResumoNuvem,
  ResumoRota,
  SEM_DADOS,
  SEM_VALOR,
  ServidorNuvem,
  formatarDataHora,
  formatarMs,
  formatarMsSeHouver,
  lugaresDoServidor,
  mensagemDeErro,
  obterResumo,
  obterServidor,
  periodoPadrao,
  validarPeriodo,
} from '../services/diagnosticoNuvemService';

const ORIGENS: { value: Origem | ''; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'app', label: 'App' },
  { value: 'web', label: 'Web' },
  { value: 'outro', label: 'Outro' },
];

const CAMPO =
  'px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

const gb = (mb: number) => `${(mb / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`;

const ServidorCard = ({ servidor, lugares }: { servidor: ServidorNuvem; lugares: number | null }) => {
  const { gunicorn, memoria, load_avg, banco } = servidor;
  const itens: [string, string][] = [
    ['Ambiente', servidor.ambiente || SEM_VALOR],
    ['CPUs', servidor.cpu_count != null ? String(servidor.cpu_count) : SEM_VALOR],
    [
      'Load (1 / 5 / 15 min)',
      load_avg
        ? load_avg.map((v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })).join(' / ')
        : SEM_VALOR,
    ],
    [
      'Gunicorn',
      gunicorn
        ? `${gunicorn.workers} workers × ${gunicorn.threads} threads${lugares !== null ? ` = ${lugares} lugares` : ''}`
        : SEM_VALOR,
    ],
    ['Memória livre', memoria ? `${gb(memoria.disponivel_mb)} de ${gb(memoria.total_mb)}` : SEM_VALOR],
    [
      'Banco',
      banco
        ? [
            banco.vendor,
            banco.tamanho,
            banco.conexoes != null ? `${banco.conexoes} de ${banco.max_connections ?? '?'} conexões` : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : SEM_VALOR,
    ],
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Server size={18} className="text-blue-600 dark:text-blue-400" />
          Servidor
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          lido em {formatarDataHora(servidor.agora)} · pid {servidor.pid}
        </span>
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {itens.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{rotulo}</dt>
            <dd className="text-sm font-semibold text-gray-900 dark:text-white">{valor}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const rotaLinha = (r: ResumoRota) => [
  r.metodo,
  <span className="font-mono text-xs">{r.rota}</span>,
  r.origem,
  formatarQuantidade(r.n),
  formatarMs(r.total_ms),
  formatarMs(r.p50_ms),
  formatarMs(r.p95_ms),
  formatarMs(r.max_ms),
  `${formatarMs(r.db_ms_medio)} (${formatarQuantidade(r.db_consultas_medio, 1)})`,
  formatarMs(r.cpu_ms_medio),
];

const DiagnosticoNuvemPage: React.FC = () => {
  const { user } = useAuthStore();
  const isMaster = user?.role === 'MASTER';

  const [periodo, setPeriodo] = useState(periodoPadrao);
  const [origem, setOrigem] = useState<Origem | ''>('');
  // digitar o ano no <input type="date"> passa por 0002, 0020, 0202...: espera parar
  const [inicio] = useDebounce(periodo.inicio, 500);
  const [fim] = useDebounce(periodo.fim, 500);
  // periodo que o backend recusaria (400) nem vai a API
  const erroPeriodo = validarPeriodo(inicio, fim);

  const [resumo, setResumo] = useState<ResumoNuvem | null>(null);
  const [servidor, setServidor] = useState<ServidorNuvem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const ultimaCarga = useRef(0);

  const carregar = useCallback(async () => {
    // resposta que chega depois de outra mais nova nao sobrescreve a tela
    const minha = ++ultimaCarga.current;
    setLoading(true);
    setError(null);
    try {
      const r = await obterResumo({ inicio, fim, origem });
      if (minha !== ultimaCarga.current) return;
      setResumo(r);
    } catch (e) {
      if (minha !== ultimaCarga.current) return;
      // sem isto o erro apareceria em cima dos numeros do periodo anterior
      setResumo(null);
      setError(mensagemDeErro(e));
    } finally {
      if (minha === ultimaCarga.current) setLoading(false);
    }
  }, [inicio, fim, origem]);

  useEffect(() => {
    if (isMaster && !erroPeriodo) carregar();
  }, [isMaster, erroPeriodo, carregar]);

  // O servidor nao depende de periodo nem de origem: lido na abertura e no Atualizar.
  // Falha dele so esconde o cartao (lugares ficam "indisponiveis"), nunca a tela.
  useEffect(() => {
    if (!isMaster) return;
    let valendo = true;
    obterServidor()
      .then((s) => {
        if (valendo) setServidor(s);
      })
      .catch(() => {
        if (valendo) setServidor(null);
      });
    return () => {
      valendo = false;
    };
  }, [isMaster, recarga]);

  // campo livre: apagar ('') entra no estado; quem barra a API e o validarPeriodo
  const mudarData = (campo: 'inicio' | 'fim') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value;
    setPeriodo((p) => ({ ...p, [campo]: valor }));
  };

  if (!isMaster) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <MainLayout>
          <div className="p-6">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2 text-amber-800 dark:text-amber-400">
              <AlertCircle size={16} />
              Apenas usuários MASTER podem ver o Diagnóstico Nuvem/App.
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  const lugares = lugaresDoServidor(servidor);
  // com periodo invalido o resumo guardado e de outro periodo: nao aparece
  const exibido = erroPeriodo ? null : resumo;
  const kpis = exibido?.kpis;
  // o lugar no gunicorn e um so para todas as origens, entao o backend mede o pico
  // sempre sobre tudo; o destaque vale com qualquer filtro, e com filtro a tela avisa
  const saturado = lugares !== null && kpis !== undefined && kpis.pico_simultaneas >= lugares;
  const todasAsOrigens = origem ? ' (todas as origens)' : '';
  const aviso = erroPeriodo ?? error;

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <MainLayout>
        <div className="flex flex-col h-full">
          {/* Header + filtros */}
          <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <CloudCog className="text-blue-600 dark:text-blue-400" />
                  Diagnóstico Nuvem/App
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Tempo de cada requisição no servidor e de cada sincronização do app (fuso de São Paulo)
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span className="block mb-1">De</span>
                  <input type="date" value={periodo.inicio} onChange={mudarData('inicio')} className={CAMPO} />
                </label>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span className="block mb-1">Até</span>
                  <input
                    type="date"
                    value={periodo.fim}
                    min={periodo.inicio}
                    onChange={mudarData('fim')}
                    className={CAMPO}
                  />
                </label>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span className="block mb-1">Origem</span>
                  <select value={origem} onChange={(e) => setOrigem(e.target.value as Origem | '')} className={CAMPO}>
                    {ORIGENS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    carregar();
                    setRecarga((n) => n + 1);
                  }}
                  disabled={loading || erroPeriodo !== null}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  Atualizar
                </button>
              </div>
            </div>
          </div>

          {aviso && (
            <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle size={16} />
              {aviso}
            </div>
          )}

          {/* recarga mantem o que ja esta na tela, so esmaecido (sem pulo de layout) */}
          <div className={`flex-1 overflow-auto px-6 py-4 space-y-6 ${loading && resumo ? 'opacity-60' : ''}`}>
            {loading && !resumo && (
              <p className="py-8 text-center text-gray-500 dark:text-gray-400">{MESSAGES.GENERIC.LOADING}</p>
            )}

            {servidor && <ServidorCard servidor={servidor} lugares={lugares} />}

            {kpis && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                <StatCard
                  icon={Activity}
                  label="Requisições"
                  value={formatarQuantidade(kpis.requisicoes)}
                  change={`banco ${formatarMs(kpis.db_ms_total)} · CPU ${formatarMs(kpis.cpu_ms_total)}`}
                  changeType="positive"
                  variant="blue"
                />
                <StatCard
                  icon={Timer}
                  label="Tempo p50 / p95"
                  value={`${formatarMsSeHouver(kpis.p50_ms, kpis.requisicoes)} / ${formatarMsSeHouver(kpis.p95_ms, kpis.requisicoes)}`}
                  change={`máx ${formatarMsSeHouver(kpis.max_ms, kpis.requisicoes)}`}
                  changeType="positive"
                  variant="purple"
                />
                {/* N226: >= lugares = toda requisicao a mais esperou na fila do gunicorn */}
                <div className={`relative rounded-xl ${saturado ? 'ring-2 ring-red-500 dark:ring-red-400' : ''}`}>
                  <StatCard
                    icon={Gauge}
                    label={`Pico de simultâneas${todasAsOrigens}`}
                    value={formatarQuantidade(kpis.pico_simultaneas)}
                    change={
                      lugares === null
                        ? 'lugares do gunicorn indisponíveis'
                        : saturado
                          ? `≥ ${lugares} lugares: requisições na fila`
                          : `de ${lugares} lugares`
                    }
                    changeType={saturado ? 'negative' : 'positive'}
                    variant="orange"
                  />
                  {saturado && (
                    <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                      <AlertTriangle size={12} />
                      Saturado
                    </span>
                  )}
                </div>
                <StatCard
                  icon={RefreshCw}
                  label="Sincronizações"
                  value={formatarQuantidade(kpis.sincronizacoes)}
                  change="reportadas pelo app"
                  changeType="positive"
                  variant="green"
                />
                <StatCard
                  icon={Clock}
                  label="Sincronização p50 / p95"
                  value={`${formatarMsSeHouver(kpis.sync_p50_ms, kpis.sincronizacoes)} / ${formatarMsSeHouver(kpis.sync_p95_ms, kpis.sincronizacoes)}`}
                  change="duração medida no app"
                  changeType="positive"
                  variant="red"
                />
              </div>
            )}

            {exibido &&
              (exibido.por_hora.length > 0 ? (
                <GraficoPorHora dados={exibido.por_hora} lugares={lugares} origem={origem} />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
                  {SEM_DADOS}
                </div>
              ))}

            {exibido && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <Tabela
                  titulo="Rotas que mais somam tempo no servidor (top 30)"
                  cabecalho={['Método', 'Rota', 'Origem', 'Requisições', 'Total', 'p50', 'p95', 'Máx', 'Banco médio (consultas)', 'CPU médio']}
                  linhas={exibido.rotas.map(rotaLinha)}
                />
              </div>
            )}

            {/* montada mesmo com periodo invalido: filtros e pagina sobrevivem a correcao */}
            <SincronizacoesTabela inicio={inicio} fim={fim} periodoValido={!erroPeriodo} recarga={recarga} />
          </div>
        </div>
      </MainLayout>
    </div>
  );
};

export default DiagnosticoNuvemPage;
